import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, Button, ServiceMark, StatusPill } from "@/components/primitives";
import { Icon } from "@/components/Icon";
import { PageHeader } from "@/components/Layout";
import { UptimeRibbon } from "@/components/UptimeRibbon";
import { IncidentRow } from "@/components/IncidentRow";
import { useSnapshot } from "@/lib/snapshot-context";
import { SEVERITY_COLOR, STATE_META } from "@/lib/format";
import type { Incident, Service, ServiceState } from "@/lib/types";
import { isAdmin } from "@/lib/auth";

export function Dashboard() {
  const navigate = useNavigate();
  const { data } = useSnapshot();
  const { services, incidents } = data;
  const activeIncidents = incidents.filter((i) => i.status !== "resolved");
  const resolved = incidents.filter((i) => i.status === "resolved");

  const counts = {
    operational: services.filter((s) => s.state === "operational").length,
    degraded: services.filter((s) => s.state === "degraded").length,
    outage: services.filter((s) => s.state === "major-outage").length,
  };
  const overallTone: "success" | "warning" | "destructive" =
    counts.outage > 0
      ? "destructive"
      : counts.degraded > 0
        ? "warning"
        : "success";

  return (
    <>
      <PageHeader crumbs={["Status Watch", "Dashboard"]} />

      <div style={{ padding: "20px 32px 0" }}>
        <StatusBanner
          tone={overallTone}
          counts={counts}
          activeCount={activeIncidents.length}
        />
      </div>

      <div
        style={{
          padding: "20px 32px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <Card>
          <CardHeader
            title="Live timeline · last 24 hours"
            description="Incidents across monitored services. Each bar is an incident; color is severity."
            right={
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <LegendDot color="var(--destructive)" label="major" />
                <LegendDot color="var(--warning)" label="minor" />
                <LegendDot color="var(--info)" label="maintenance" />
                <span style={{ marginLeft: 8 }}>
                  <Button size="sm" variant="ghost" icon="caret">
                    24h
                  </Button>
                </span>
              </div>
            }
          />
          <Timeline24h
            services={services}
            incidents={incidents}
            onPick={(id) => navigate(`/services/${id}`)}
          />
        </Card>

        <div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                Services
              </h2>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--muted-foreground)",
                  marginTop: 2,
                }}
              >
                {services.length} monitored · {counts.operational} operational ·{" "}
                {counts.degraded + counts.outage} with active issue
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Button size="sm" variant="outline" icon="filter">
                Filter
              </Button>
              {isAdmin() && (
                <Button
                  size="sm"
                  variant="primary"
                  icon="plus"
                  onClick={() => navigate("/admin/add")}
                >
                  Add service
                </Button>
              )}
            </div>
          </div>
          <ServiceGrid
            services={services}
            onPick={(id) => navigate(`/services/${id}`)}
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr",
            gap: 16,
          }}
        >
          <Card>
            <CardHeader
              title="Active incidents"
              description={`${activeIncidents.length} ongoing across ${
                new Set(activeIncidents.map((i) => i.serviceId)).size
              } services`}
            />
            <div>
              {activeIncidents.length === 0 && (
                <Empty msg="No active incidents." />
              )}
              {activeIncidents.map((inc) => (
                <IncidentRow
                  key={inc.id}
                  incident={inc}
                  onPick={() => navigate(`/incidents/${inc.id}`)}
                />
              ))}
            </div>
          </Card>
          <Card>
            <CardHeader
              title="Recently resolved"
              description="Closed in the last 7 days"
              right={
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/incidents");
                  }}
                  style={{
                    fontSize: 12,
                    color: "var(--muted-foreground)",
                    textDecoration: "none",
                  }}
                >
                  View all →
                </a>
              }
            />
            <div>
              {resolved.slice(0, 4).map((inc) => (
                <IncidentRow
                  key={inc.id}
                  incident={inc}
                  compact
                  onPick={() => navigate(`/incidents/${inc.id}`)}
                />
              ))}
              {resolved.length === 0 && <Empty msg="Nothing to show yet." />}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div
      style={{
        padding: 20,
        textAlign: "center",
        fontSize: 12,
        color: "var(--muted-foreground)",
        borderTop: "1px solid var(--border)",
      }}
    >
      {msg}
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11,
        color: "var(--muted-foreground)",
      }}
    >
      <span
        style={{ width: 8, height: 8, borderRadius: 2, background: color }}
      />
      {label}
    </span>
  );
}

interface BannerProps {
  tone: "success" | "warning" | "destructive";
  counts: { operational: number; degraded: number; outage: number };
  activeCount: number;
}

function StatusBanner({ tone, counts, activeCount }: BannerProps) {
  const cfg = {
    success: {
      bg: "color-mix(in oklch, var(--success) 8%, var(--card))",
      ring: "color-mix(in oklch, var(--success) 30%, transparent)",
      iconBg: "var(--success)",
      title: "All systems operational",
      sub: `${counts.operational} services monitored — no active incidents.`,
      icon: "check",
    },
    warning: {
      bg: "color-mix(in oklch, var(--warning) 8%, var(--card))",
      ring: "color-mix(in oklch, var(--warning) 35%, transparent)",
      iconBg: "var(--warning)",
      title: `${activeCount} active incidents`,
      sub: `${counts.degraded} services degraded — partial impact to dependents.`,
      icon: "alert",
    },
    destructive: {
      bg: "color-mix(in oklch, var(--destructive) 8%, var(--card))",
      ring: "color-mix(in oklch, var(--destructive) 35%, transparent)",
      iconBg: "var(--destructive)",
      title: `Major outage — ${activeCount} active incidents`,
      sub: `${counts.outage} service${counts.outage === 1 ? "" : "s"} with major impact, ${counts.degraded} degraded.`,
      icon: "alert",
    },
  }[tone];

  return (
    <div
      style={{
        background: cfg.bg,
        borderRadius: 14,
        padding: "16px 18px",
        boxShadow: `0 0 0 1px ${cfg.ring}`,
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: cfg.iconBg,
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name={cfg.icon} size={22} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em" }}
        >
          {cfg.title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "var(--muted-foreground)",
            marginTop: 2,
          }}
        >
          {cfg.sub}
        </div>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <Stat
          label="Operational"
          num={counts.operational}
          color="var(--success)"
        />
        <div style={{ width: 1, height: 28, background: "var(--border)" }} />
        <Stat label="Degraded" num={counts.degraded} color="var(--warning)" />
        <div style={{ width: 1, height: 28, background: "var(--border)" }} />
        <Stat label="Major" num={counts.outage} color="var(--destructive)" />
      </div>
    </div>
  );
}

function Stat({
  label,
  num,
  color,
}: {
  label: string;
  num: number;
  color: string;
}) {
  return (
    <div style={{ textAlign: "center", minWidth: 56 }}>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          color,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        {num}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "var(--muted-foreground)",
          marginTop: 4,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
    </div>
  );
}

interface TimelineProps {
  services: Service[];
  incidents: Incident[];
  onPick: (serviceId: string) => void;
}

function Timeline24h({ services, incidents, onPick }: TimelineProps) {
  const { now, fmt } = useSnapshot();
  const startMs = now.getTime() - 24 * 3600 * 1000;
  const endMs = now.getTime();
  const span = endMs - startMs;

  const tickLabels: { h: number; x: number; lbl: string }[] = [];
  for (let h = 24; h >= 0; h -= 3) {
    const d = new Date(now.getTime() - h * 3600 * 1000);
    const lbl = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    });
    tickLabels.push({
      h,
      x: ((d.getTime() - startMs) / span) * 100,
      lbl: h === 0 ? "now" : lbl,
    });
  }

  const rowH = 32;
  const labelW = 130;

  return (
    <div style={{ padding: "12px 16px 14px" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${labelW}px 1fr`,
          gap: 12,
        }}
      >
        <div />
        <div style={{ position: "relative", height: 16, marginBottom: 4 }}>
          {tickLabels.map((t) => (
            <div
              key={t.h}
              style={{
                position: "absolute",
                left: `${t.x}%`,
                top: 0,
                transform: "translateX(-50%)",
                fontSize: 10,
                color: "var(--muted-foreground)",
                fontFamily: "var(--font-mono)",
                whiteSpace: "nowrap",
              }}
            >
              {t.lbl}
            </div>
          ))}
        </div>

        {services.map((svc) => {
          const svcIncidents = incidents.filter((i) => i.serviceId === svc.id);
          return (
            <Fragment key={svc.id}>
              <div
                role="button"
                onClick={() => onPick(svc.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  height: rowH,
                  cursor: "pointer",
                }}
              >
                <ServiceMark service={svc} size={22} />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.1 }}
                  >
                    {svc.name}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: "var(--muted-foreground)",
                      lineHeight: 1.1,
                      marginTop: 2,
                    }}
                  >
                    {STATE_META[svc.state]?.label ?? svc.state}
                  </span>
                </div>
              </div>
              <TimelineRow
                incidents={svcIncidents}
                startMs={startMs}
                endMs={endMs}
                height={rowH}
                onPick={() => onPick(svc.id)}
                fmtDuration={fmt.duration}
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

interface RowProps {
  incidents: Incident[];
  startMs: number;
  endMs: number;
  height: number;
  onPick: () => void;
  fmtDuration: (a: string, b?: string | null) => string;
}

function TimelineRow({
  incidents,
  startMs,
  endMs,
  height,
  onPick,
  fmtDuration,
}: RowProps) {
  const span = endMs - startMs;
  return (
    <div
      style={{
        position: "relative",
        height,
        background: "var(--muted)",
        borderRadius: 6,
        overflow: "hidden",
      }}
    >
      {[3, 6, 9, 12, 15, 18, 21].map((h) => {
        const t = endMs - h * 3600 * 1000;
        const x = ((t - startMs) / span) * 100;
        return (
          <div
            key={h}
            style={{
              position: "absolute",
              left: `${x}%`,
              top: 0,
              bottom: 0,
              width: 1,
              background:
                "color-mix(in oklch, var(--border) 60%, transparent)",
            }}
          />
        );
      })}

      {incidents.map((inc) => {
        const a = new Date(inc.startedAt).getTime();
        const b = inc.resolvedAt ? new Date(inc.resolvedAt).getTime() : endMs;
        if (b < startMs) return null;
        const left = Math.max(
          0,
          ((Math.max(a, startMs) - startMs) / span) * 100,
        );
        const right = Math.min(100, ((b - startMs) / span) * 100);
        const width = Math.max(0.7, right - left);
        const isActive = inc.status !== "resolved";
        const color =
          inc.severity === "major" ? "var(--destructive)" : "var(--warning)";
        return (
          <div
            key={inc.id}
            title={`${inc.title} · ${fmtDuration(inc.startedAt, inc.resolvedAt)}`}
            onClick={(e) => {
              e.stopPropagation();
              onPick();
            }}
            style={{
              position: "absolute",
              left: `${left}%`,
              width: `${width}%`,
              top: 4,
              bottom: 4,
              backgroundColor: color,
              borderRadius: 4,
              cursor: "pointer",
              boxShadow: isActive
                ? `inset 0 0 0 1.5px color-mix(in oklch, ${color} 60%, #000), 0 0 0 1px ${color}`
                : "none",
              opacity: isActive ? 1 : 0.55,
              backgroundImage: isActive
                ? `repeating-linear-gradient(45deg, transparent 0 4px, color-mix(in oklch, ${color} 60%, #fff) 4px 6px)`
                : "none",
            }}
          />
        );
      })}

      <div
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: 2,
          background: "var(--primary)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: -3,
          top: -3,
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "var(--primary)",
          boxShadow:
            "0 0 0 3px color-mix(in oklch, var(--primary) 25%, transparent)",
        }}
      />
    </div>
  );
}

function ServiceGrid({
  services,
  onPick,
}: {
  services: Service[];
  onPick: (id: string) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 12,
      }}
    >
      {services.map((svc) => (
        <ServiceCard key={svc.id} service={svc} onPick={() => onPick(svc.id)} />
      ))}
    </div>
  );
}

function ServiceCard({
  service,
  onPick,
}: {
  service: Service;
  onPick: () => void;
}) {
  const { data } = useSnapshot();
  const uptime = data.uptime[service.id] ?? [];
  return (
    <div
      role="button"
      onClick={onPick}
      style={{
        background: "var(--card)",
        borderRadius: 12,
        padding: 14,
        cursor: "pointer",
        boxShadow:
          "0 0 0 1px color-mix(in oklch, var(--foreground) 8%, transparent)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        borderLeft:
          service.state === "operational"
            ? "3px solid transparent"
            : `3px solid ${SEVERITY_COLOR[service.state as ServiceState]}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <ServiceMark service={service} size={30} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}
          >
            {service.name}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--muted-foreground)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon
              name={service.source === "rss" ? "rss" : "webhook"}
              size={11}
            />
            {service.domain}
          </div>
        </div>
        <StatusPill state={service.state} />
      </div>

      <div
        style={{
          fontSize: 12,
          color: "var(--muted-foreground)",
          minHeight: 18,
        }}
      >
        {service.summary}
      </div>

      <UptimeRibbon uptime={uptime} compact />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 11,
          color: "var(--muted-foreground)",
          paddingTop: 6,
          borderTop: "1px solid var(--border)",
        }}
      >
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {service.uptime90}% · 90d
        </span>
        <span>checked {service.lastChecked}</span>
      </div>
    </div>
  );
}
